import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { Input } from '../../form';
import { fonts, colors } from '../../styles';
import { Icon } from '../../ui_elements/icons';
import { DeleteConfirmation } from '../../ui_elements';
import customIcons from '../../styles/icons';

const RowBase = styled('div')`
  position: relative;
  display: flex;
  border-bottom: 1px solid ${colors.grey.secondaryDark};
  @media screen and (max-width: 1000px) {
    flex-direction: column;
    min-width: 0 !important;
    margin-bottom: 20px;
    padding: 10px 0;
    border-bottom: 0;
    border: 1px solid ${colors.grey.secondary};
    box-shadow: 0 1px 0px ${colors.grey.secondaryDark};
    box-sizing: border-box;
  }
`;

const allowDeleteStyles = css`
  [class*='HeaderRow'] div:last-child,
  [data-title='Delete'] {
    width: 85px;
    text-align: center;
  }
  [class*='Overlay'] {
    border-radius: 0;
  }
  i[type='x']:hover {
    cursor: pointer;
  }
`;

export const Table = styled('div')`
  overflow: auto;
  ${(props) => props.showDelete && allowDeleteStyles};
`;

const AlternatingRow = styled(RowBase)`
  ${(props) =>
    props.even &&
    `
      background: ${colors.grey.secondaryLight};
    `}
`;

export const RowContainer = (props) => {
  const { showDeleteConfirmation, deleteCancel, deleteConfirm, children } = props;

  return (
    <AlternatingRow {...props}>
      <DeleteConfirmation
        isOpen={showDeleteConfirmation}
        onCancel={deleteCancel}
        onDelete={deleteConfirm}
        size="short"
      >
        {children}
      </DeleteConfirmation>
    </AlternatingRow>
  );
};

RowContainer.defaultProps = {
  showDeleteConfirmation: false,
  deleteCancel: () => {},
  deleteConfirm: () => {},
  children: null,
};

RowContainer.propTypes = {
  showDeleteConfirmation: PropTypes.bool,
  deleteCancel: PropTypes.func,
  deleteConfirm: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export const HeaderRow = styled(RowBase)`
  background: ${colors.white};
  box-shadow: 0 1px 1px 0px ${colors.grey.secondary};
  color: ${colors.black};
  @media screen and (max-width: 1000px) {
    display: none !important;
  }
`;

export const CellInnerWrapper = styled('div')`
  @media screen and (max-width: 1000px) {
    padding-left: 170px;
    display: block;
    text-align: left;
  }
  @media screen and (max-width: 480px) {
    padding-left: 0;
  }
`;

export const Cell = styled('div')`
  padding: 7px 15px;
  border-left: 1px solid ${colors.grey.secondary};
  position: relative;
  text-align: left;
  box-sizing: border-box;
  @media screen and (max-width: 1000px) {
    border-right: 0;
    width: 100% !important;
    padding: 0 10px 3px;
    max-width: none !important;
    &[data-title]::before {
      content: attr(data-title);
      display: inline-block;
      max-width: 170px;
      padding: 0 10px;
      position: absolute;
      top: 0;
      left: 0;
      font-size: ${fonts.sans.size.small};
      font-weight: ${fonts.sans.weight.bold};
    }
  }
  @media screen and (max-width: 480px) {
    margin-bottom: 10px;
    padding: 0 10px;
    &[data-title]::before {
      display: block;
      max-width: none;
      position: relative;
      padding: 0;
      line-height: 18px;
    }
  }
`;

export const TotalRow = styled(RowBase)`
  font-weight: ${fonts.sans.weight.bold};
  text-transform: uppercase;
  color: ${colors.grey.primary};
  @media screen and (max-width: 1000px) {
    box-shadow: none;
    border: 0;
    padding: 0;
    margin-bottom: 15px;
    ${Cell} {
      padding: 0;
      margin: 0;
    }
  }
`;

export const DeleteCell = (props) => {
  const { onDelete } = props;

  return (
    <Cell data-title="Delete">
      <CellInnerWrapper>
        <Icon type="x" onClick={onDelete} />
      </CellInnerWrapper>
    </Cell>
  );
};

DeleteCell.propTypes = {
  onDelete: PropTypes.func.isRequired,
};

export const SelectFilter = styled('select')`
  border: none;
  background: transparent;
  font-family: ${fonts.sans.family};
  font-size: ${fonts.sans.size.normal};
  font-weight: ${fonts.sans.weight.bold};
  color: ${colors.blue.primary};
  height: 30px;
  appearance: none;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  opacity: 1;
  border-radius: 0;
  padding-right: 20px;
  &:hover {
    cursor: pointer;
  }
`;

export const SelectFilterWrapper = styled('div')`
  position: relative;
  &::after {
    ${fonts.icons}
    content: '${customIcons['angle-down']}';
    position: absolute;
    right: 0;
    top: 0;
    line-height: 30px;
    display: block;
    font-size: ${fonts.sans.size.xlarge};
    pointer-events: none;
  }
`;

export const SearchFilter = styled(Input)`
  min-height: 30px;
  border-radius: 0;
  margin: 0;
  border: 0;
  padding: 0;
  font-size: ${fonts.sans.size.normal};
  font-weight: ${fonts.sans.weight.bold};
  color: ${colors.blue.primary};
  &:hover,
  &:focus {
    border: 0;
  }
  &::placeholder {
    color: ${colors.grey.altDark};
  }
`;
